import { useEffect, useRef } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTournament } from '@/hooks/useTournament'
import { useGuest } from '@/hooks/useGuest'
import { joinLinkToken } from '@/lib/guest'
import { TournamentHeader } from '@/components/zambepong/TournamentHeader'
import { JoinCard } from '@/components/zambepong/JoinCard'
import { ParticipantsList } from '@/components/zambepong/ParticipantsList'
import { TournamentTabs } from '@/components/zambepong/TournamentTabs'
import { LiveDraw } from '@/components/zambepong/LiveDraw'
import { LoadError } from '@/components/zambepong/LoadError'
import { LocalizedLink } from '@/components/LocalizedLink'

/** Pagina di un torneo: iscrizione (o riconoscimento come ospite già iscritto) finché è in `registration`,
 * avviso di chiusura in `closed`; le tab di girone/classifica/eliminatorie (Task 3) montano su `active`/`completed`. */
export function TournamentPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const { data, error, isLive, reload, version } = useTournament(id)
  const { guest, me, join, adopt, forget, reload: reloadGuest } = useGuest(id)
  const [, setSearchParams] = useSearchParams()
  // Guardia contro il doppio effetto di React StrictMode in sviluppo: un solo tentativo di adozione per montaggio.
  const hasTriedJoinLink = useRef(false)

  useEffect(() => {
    if (hasTriedJoinLink.current) return
    hasTriedJoinLink.current = true
    const token = joinLinkToken(location.search)
    if (!token) return
    adopt(token)
      .catch(() => { /* token non valido: resta la card di iscrizione, nessun avviso rumoroso */ })
      // Via React Router (non `history.replaceState`): altrimenti la sua location terrebbe ancora `join=`
      // e il primo `setSearchParams` di una tab lo riscriverebbe nell'URL. Resta l'eventuale `?tab=`.
      .finally(() => setSearchParams((params) => {
        const next = new URLSearchParams(params)
        next.delete('join')
        return next
      }, { replace: true }))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- un solo tentativo al montaggio, vedi hasTriedJoinLink
  }, [])

  // Saldo e registro dell'ospite dal vivo: `/me` si rilegge a ogni messaggio del torneo (`version`),
  // saltando il primo render (lo carica già `useGuest` al montaggio).
  const hasSeenFirstVersion = useRef(false)
  useEffect(() => {
    if (!hasSeenFirstVersion.current) {
      hasSeenFirstVersion.current = true
      return
    }
    void reloadGuest()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al cambio di `version`, non di `reloadGuest`
  }, [version])

  if (!data && error) {
    return (
      <main id="main" className="mx-auto max-w-7xl px-4 pt-28 pb-section sm:px-6 lg:px-8">
        <LoadError error={error} className="text-ink">
          <LocalizedLink to="zambePong" className="mt-4 inline-block font-body text-sm font-semibold text-sea-ink underline-offset-4 hover:underline">
            ← {t('zambePong.error.back')}
          </LocalizedLink>
        </LoadError>
      </main>
    )
  }
  if (!data) return null

  const { tournament, participants, teams } = data
  const wallet = me ? { name: me.participant.name, balance: me.participant.balance } : undefined

  async function handleJoin(name: string, website: string) {
    await join(name, website)
    reload()
  }

  return (
    <main id="main" className="mx-auto max-w-7xl px-4 pt-20 pb-section sm:px-6 md:pt-28 lg:px-8">
      <TournamentHeader tournament={tournament} isLive={isLive} wallet={wallet} />

      {tournament.status === 'registration' && (
        <div className="mt-8 grid gap-8 lg:grid-cols-2 lg:gap-10">
          <div>
            {guest ? (
              <div className="rounded-xl border border-ink/10 bg-paper p-5 shadow-card">
                <p className="font-display font-semibold text-3xl text-ink">{t('zambePong.join.joined', { name: guest.name })}</p>
                {me && <p className="mt-2 font-body text-sm text-ink-soft">{t('zambePong.wallet.coins', { count: me.participant.balance })}</p>}
                <button
                  type="button"
                  onClick={forget}
                  className="mt-4 inline-flex min-h-11 items-center font-body text-sm font-semibold text-sea-ink underline-offset-4 hover:underline"
                >
                  {t('zambePong.join.forget')}
                </button>
              </div>
            ) : (
              <JoinCard onJoin={handleJoin} />
            )}
          </div>
          <div>
            <ParticipantsList participants={participants} teams={teams} />
            <p className="mt-6 font-body text-sm text-ink-soft">{t('zambePong.join.coinsIntro', { coins: tournament.coinsInitial })}</p>
          </div>
        </div>
      )}

      {tournament.status === 'closed' && (
        <div className="mt-8 grid gap-8 lg:grid-cols-2 lg:gap-10">
          <p role="status" className="rounded-xl border border-ink/10 bg-sun/15 p-5 font-body text-lg text-ink shadow-card">
            {t('zambePong.closed')}
          </p>
          <ParticipantsList participants={participants} teams={teams} />
        </div>
      )}

      {(tournament.status === 'active' || tournament.status === 'completed') && (
        // Durante l'estrazione live squadre e partite non ci sono ancora: c'è Zambe che le pesca.
        data.draw ? (
          <div className="mt-8">
            <LiveDraw draw={data.draw} participants={participants} onReload={reload} />
          </div>
        ) : (
          <div className="mt-8" data-testid="tabs">
            <TournamentTabs detail={data} me={me} tournamentId={id!} version={version} />
          </div>
        )
      )}
    </main>
  )
}
